import { addDays, differenceInCalendarDays } from 'date-fns';
import type { Sprint } from '@/types/sprint';

/**
 * Returns true if a sprint overlaps with the given date range.
 * Overlap condition: sprint ends after range start AND sprint starts before range end.
 */
export function isSprintInRange(
  sprint: Sprint,
  range: { start: Date; end: Date }
): boolean {
  return sprint.end_date >= range.start && sprint.start_date <= range.end;
}

/**
 * Returns true if today falls within the sprint's start and end dates (inclusive).
 */
export function isCurrentSprint(sprint: Sprint): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(sprint.start_date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(sprint.end_date);
  end.setHours(23, 59, 59, 999);
  return today >= start && today <= end;
}

/**
 * Sorts sprints by start_date, detects overlaps (console.warn) and gaps > 1 day (console.info).
 * Returns the sorted array for use in rendering. Designed to be called after import so unusual
 * patterns are surfaced in the browser console without blocking the UI.
 */
export function handleSprintEdgeCases(sprints: Sprint[]): Sprint[] {
  const sorted = [...sprints].sort(
    (a, b) => a.start_date.getTime() - b.start_date.getTime()
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const curr = sorted[i]!;
    const next = sorted[i + 1]!;

    if (curr.end_date >= next.start_date) {
      console.warn(`Sprint overlap detected: ${curr.name} and ${next.name}`);
    } else {
      const gapDays = differenceInCalendarDays(next.start_date, curr.end_date);
      if (gapDays > 1) {
        console.info(`Gap of ${gapDays} days between ${curr.name} and ${next.name}`);
      }
    }
  }

  return sorted;
}

/**
 * Generates a list of stub sprints for testing and scaffolding.
 * Each sprint is 14 calendar days long (2-week sprints).
 * Named "Sprint N" starting from sprint_number 1.
 *
 * @param count - Number of sprints to generate
 * @param startDate - Start date of the first sprint
 */
export function generateStubSprints(count: number, startDate: Date): Sprint[] {
  const sprints: Sprint[] = [];

  for (let i = 0; i < count; i++) {
    const sprintStart = addDays(startDate, i * 14);
    const sprintEnd = addDays(sprintStart, 13);

    sprints.push({
      id: i + 1,
      user_id: 'stub',
      name: `Sprint ${i + 1}`,
      sprint_number: i + 1,
      start_date: sprintStart,
      end_date: sprintEnd,
    });
  }

  return sprints;
}
